import {
  action,
  observable,
} from 'mobx';
import CharityDonationApi from '../../api/CharityDonationApi';
import TransactionRepository from '../TransactionRepository';
import { ICharityDonation } from '../../api/dataMapper/donationApiMapper';
import STATUS from '../../constants/Status';
import normalizeApiErrors from '../../helpers/normalizeApiErrors';
import NotificationsCollection, { messageTimeoutDefaultMs } from '../../domain/entities/Notification/NotificationsCollection';
import Transaction from '../../domain/entities/Transaction/Transaction';
import { CART_NOTIFICATIONS, NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../constants/Notifications';

interface ICharityDonationRepositoryConfig {
  charityDonationApi: CharityDonationApi;
  notificationsCollection: NotificationsCollection;
  transactionRepository: TransactionRepository;
}

class CharityDonationRepository  {
  private charityDonationApi: CharityDonationApi;

  private transactionRepository: TransactionRepository;

  private notificationsCollection: NotificationsCollection;

  @observable charityDonations: ICharityDonation[] = [];

  @observable charityLoadedStatus: STATUS = STATUS.IDLE;

  @observable donationUpdateStatus: STATUS = STATUS.IDLE;

  constructor({
    charityDonationApi,
    notificationsCollection,
    transactionRepository,
  }: ICharityDonationRepositoryConfig) {
    this.notificationsCollection = notificationsCollection;
    this.charityDonationApi = charityDonationApi;
    this.transactionRepository = transactionRepository;
  }

  public async loadDonations(): Promise<ICharityDonation[]> {
    if (this.charityDonations.length === 0) {
      try {
        this.setCharityLoadedStatus(STATUS.LOADING);
        this.charityDonations = await this.charityDonationApi.loadCharityDonations();
        this.setCharityLoadedStatus(STATUS.LOADED);
      } catch (err) {
        this.setCharityLoadedStatus(STATUS.ERROR);
        this.handleError(err);
      }
    }

    return this.charityDonations;
  }

  public async addDonation(skuId: number) : Promise<void> {
    try {
      this.setDonationUpdateStatus(STATUS.LOADING);
      const transactionResponse = await this.charityDonationApi.addDonation(skuId);
      this.transactionRepository.transaction.updateTransaction(Transaction.fromJSON(transactionResponse));
      this.setDonationUpdateStatus(STATUS.LOADED);
    } catch (err) {
      this.setDonationUpdateStatus(STATUS.ERROR);
      this.handleError(err);
    }
  }

  public async removeDonations(skuIds: number[]): Promise<void> {
    try {
      this.setDonationUpdateStatus(STATUS.LOADING);
      const transactionResponse = await this.charityDonationApi.removeDonations(skuIds);
      this.transactionRepository.transaction.updateTransaction(Transaction.fromJSON(transactionResponse));
      this.setDonationUpdateStatus(STATUS.LOADED);

      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.DONATIONS,
        key: CART_NOTIFICATIONS.DONATION_REMOVED,
        timeout: messageTimeoutDefaultMs,
        type: NOTIFICATION_TYPE.SUCCESS,
      });
    } catch (err) {
      this.setDonationUpdateStatus(STATUS.ERROR);
      this.handleError(err);
    }
  }

  @action setCharityLoadedStatus(status: STATUS): void {
    this.charityLoadedStatus = status;
  }

  @action setDonationUpdateStatus(status: STATUS): void {
    this.donationUpdateStatus = status;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleError = (error: any): void => {
    const normalizedErrors = error?.key && error?.type && !error.errorResponse
      ? [error]
      :  normalizeApiErrors(error,  NOTIFICATION_GROUP.DONATIONS);

    this.notificationsCollection.addNotifications(normalizedErrors);
  };
}

export default CharityDonationRepository;
